import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { AdvancedSearchHttpService } from 'app/advanced-search/services/advanced-search-http.service';
import { GridComponent } from '@iv/shared/components/grid/grid.component';
import {
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Destroyer } from 'app/common/utils';
import {
  debounceTime,
  finalize,
  takeUntil
} from 'rxjs/operators';
import { getHttpParams } from '@iv/shared/utils';
import {
  advancedSearchGridColumns,
  marketsFutures,
  marketsFuturesCalc,
  marketsStocks,
  marketsOIC,
} from 'app/advanced-search/mock';
import { IMarket } from 'app/advanced-search/interfaces/advances-search.interface';
import { locale } from 'app/advanced-search/components/advanced-search/locale';
import {
  Future,
  Instrument
} from '@iv/model/instrument';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { IStockFutureResponse } from 'app/core/interfaces/stock-future-list.interface';
import { GridColumns } from '@iv/shared/components/grid/grid-column';
import { BehaviorSubject } from 'rxjs';

type matchingType = 'CONTAINS' | 'BEGIN' | 'EXACTLY';

@Component({
  selector: 'iv-live-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedSearchComponent extends Destroyer implements OnInit, OnDestroy {

  constructor(
    private advancedSearchHttpService: AdvancedSearchHttpService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(AppStore) private store: Redux.Store<AppState>
  ) {
    super();
  }

  @Input()
  public hasMultipleChoice = true;
  @Input()
  private isFuture: boolean;
  @Input()
  private isCalc: boolean;

  @Output()
  public closeDialog = new EventEmitter();
  @Output()
  private addSymbols = new EventEmitter<any>();
  @Output()
  private calculatorButtonClicked = new EventEmitter<any>();

  @ViewChild(GridComponent, {static: false})
  private dataGrid!: GridComponent;

  public form = new FormGroup({
    market: new FormControl(null, Validators.required),
    instrument: new FormControl(),
    exchange: new FormControl(),
    company: new FormControl(),
    matchingTypeControl: new FormControl(),
    currentPage: new FormControl(1)
  });

  public visibleColumns = ['symbol', 'company', 'exchange', 'region'];
  public gridColumns: GridColumns = advancedSearchGridColumns;
  public matchingTypes: matchingType[] = ['CONTAINS', 'BEGIN', 'EXACTLY'];
  public locale = locale;
  public markets: IMarket[];
  public autocompleteItems: Instrument[] | Future[];
  public result = [];
  public pagesCount = 0;
  public $isLoading = new BehaviorSubject(false);

  private pageSize = 10;

  public ngOnInit(): void {
    if (this.hasMultipleChoice) {
      this.visibleColumns.unshift('checkbox');
    }

    this.fillMarkets();
    this.fillForm();
    this.formSubscription();
    this.loadingSubscription();
    this.loadData();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public searchData() {
    this.form.controls.currentPage.setValue(1, { emitEvent: false });
    this.loadData();
  }

  public onPageButtonClick(page) {
    this.changeDetectorRef.detectChanges();
    this.form.get('currentPage').setValue(page);
  }

  /***Grid events***/

  public toggleAllRowsSelection(selection: boolean) {
    this.result.forEach(stock => stock.selected = selection);
    this.result = this.result.splice(0);
  }

  public onCheckboxClick(event) {
    const row = this.result.find(stock => {
      if (stock?.stockId) {
        return stock.stockId === event.stockId;
      }

      return stock.underlyingId === event.underlyingId;
    });
    row.selected = !row.selected;
    this.result = this.result.splice(0);
  }

  public onSymbolClick(event) {
    this.addSymbols.emit(event);
  }

  public onAddSymbolsClick() {
    this.addSymbols.emit(this.getSelectedInstrumentIds());
  }

  private loadData() {
    this.$isLoading.next(true);
    const { instrument, exchange, company, market, matchingTypeControl, currentPage } = this.form.value;
    const params = getHttpParams({
      sortField: 'SYMBOL',
      symbol: instrument,
      exchange,
      [this.isFuture ? 'name' : 'company']: company,
      region: market.id,
      matchingType: matchingTypeControl,
      pageSize: this.pageSize.toString(),
      page: (currentPage - 1).toString()
    });

    this.advancedSearchHttpService.getListEntities(params, this.isFuture)
      .pipe(finalize(() => this.$isLoading.next(false)))
      .subscribe((data: IStockFutureResponse<Instrument[] | Future[]>) => {
        this.result = data.page;
        this.pagesCount = data.pagesTotal;
        this.refreshDataGrid(0);
        this.changeDetectorRef.detectChanges();
      });
  }

  private refreshDataGrid(delay?) {
    if (this.dataGrid !== undefined) {
      this.dataGrid.refresh(delay);
    }
  }

  private getSelectedInstrumentIds() {
    return this.result.filter(instrument => {
      return instrument.selected === true;
    }).map(item => {
      if (item?.stockId) {
        return item.stockId;
      }

      return item.underlyingId;
    });
  }

  private fillMarkets(): void {
    this.markets = this.isFuture ? this.isCalc ? marketsFuturesCalc : marketsFutures : marketsStocks;
    if (location.hash.startsWith('#/oic/')) {
      this.markets = marketsOIC;
    }
    advancedSearchGridColumns.company.dataField = this.isFuture ? 'name' : 'company';
    advancedSearchGridColumns.exchange.dataField = this.isFuture ? 'code' : 'exchange';
  }

  private fillForm(): void {
    this.form.get('market').setValue(this.markets[0]);
    this.form.get('matchingTypeControl').setValue(this.matchingTypes[0]);
  }

  private formSubscription(): void {
    this.form.get('currentPage').valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadData());
  }

  private loadingSubscription(): void {
    this.$isLoading
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading: boolean) => isLoading
        ? this.form.disable({onlySelf: true, emitEvent: false})
        : this.form.enable({onlySelf: true, emitEvent: false})
      );
  }
}
